import React from 'react';
import { AlertCircle, Copy, CheckCircle } from 'lucide-react';

const TestCredentials = () => {
  const [copiedField, setCopiedField] = React.useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const testAccounts = [
    {
      role: 'Admin',
      email: 'admin@playbacktoken.com',
      password: 'Admin123!',
      description: 'Full system access - can manage all clubs, volunteers, bundles, and view all reports',
      color: '#dc2626'
    },
    {
      role: 'Supervisor',
      email: 'testsupervisor@club1.com',
      password: 'Supervisor123!',
      description: 'Club supervisor linked to "london homeless charity" - can approve volunteer hours and view club earnings',
      color: '#16a34a'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
            PlayBack Token - Test Credentials
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>
            Use these accounts to test different user roles and features
          </p>
        </div>

        {/* Warning Banner */}
        <div style={{ 
          background: '#fef2f2', 
          border: '2px solid #fecaca', 
          borderRadius: '8px', 
          padding: '16px 20px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <AlertCircle size={24} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
              Internal Testing Only
            </p>
            <p style={{ color: '#7f1d1d', fontSize: '0.95rem', margin: 0 }}>
              These credentials are for testing purposes only. Do not share publicly or use in production.
            </p>
          </div>
        </div>

        {/* Credentials Cards */}
        <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
          {testAccounts.map((account, index) => (
            <div 
              key={index}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: `3px solid ${account.color}`,
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              {/* Role Badge */}
              <div style={{ 
                display: 'inline-block',
                background: account.color,
                color: 'white',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                {account.role}
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '6px' 
                }}>
                  Email
                </label>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={account.email}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      background: '#f9fafb',
                      color: '#111827',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    onClick={() => copyToClipboard(account.email, `${index}-email`)}
                    style={{
                      padding: '10px 12px',
                      background: copiedField === `${index}-email` ? '#16a34a' : account.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {copiedField === `${index}-email` ? (
                      <CheckCircle size={18} />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Password Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  color: '#374151',
                  marginBottom: '6px' 
                }}>
                  Password
                </label>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={account.password}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      background: '#f9fafb',
                      color: '#111827',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    onClick={() => copyToClipboard(account.password, `${index}-password`)}
                    style={{
                      padding: '10px 12px',
                      background: copiedField === `${index}-password` ? '#16a34a' : account.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {copiedField === `${index}-password` ? (
                      <CheckCircle size={18} />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Description */}
              <div style={{
                padding: '12px',
                background: '#f9fafb',
                borderRadius: '6px',
                borderLeft: `4px solid ${account.color}`
              }}>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: '#4b5563',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  {account.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div style={{
          marginTop: '48px',
          padding: '24px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>
            Quick Links
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a 
              href="/workflow" 
              style={{
                padding: '10px 20px',
                background: '#7c3aed',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#6d28d9'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#7c3aed'}
            >
              📋 Platform Workflow Guide
            </a>
            <a 
              href="/login" 
              style={{
                padding: '10px 20px',
                background: '#dc2626',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
            >
              Go to Login Page →
            </a>
            <a 
              href="/" 
              style={{
                padding: '10px 20px',
                background: '#f3f4f6',
                color: '#111827',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s',
                border: '2px solid #e5e7eb'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
            >
              Go to Homepage
            </a>
          </div>
        </div>

        {/* Footer Notes */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          background: '#f0f9ff',
          border: '2px solid #bfdbfe',
          borderRadius: '8px'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e40af', marginBottom: '12px' }}>
            Testing Notes:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', fontSize: '0.95rem', lineHeight: '1.8' }}>
            <li>Click the copy icon next to any field to copy it to your clipboard</li>
            <li>Admin account has access to all features including user management and analytics</li>
            <li>Supervisor account is linked to "london homeless charity" club with existing data</li>
            <li>To test customer/company features, create a new customer account through the admin dashboard</li>
            <li>All passwords meet security requirements (8+ chars, uppercase, lowercase, number, special char)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TestCredentials;
